import { useState, useEffect, useRef } from "react";
import {
  useGetProfile,
  ApiError,
  useListClasses,
  getListClassesQueryKey,
  getListTimetablesQueryKey,
  getListStudentsQueryKey,
} from "@workspace/api-client-react";
import {
  useProgramRegisters,
  programRegistersKey,
} from "@/lib/program-registration";
import { getApiErrorBody, getApiErrorMessage } from "@/lib/api-error";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { useCreateCheckout, useOpenPortal } from "@/hooks/useStripe";
import { getProPriceId } from "@/lib/stripe-price";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { REGION_CONFIG, ALL_REGIONS, type Region } from "@/lib/region-config";
import { useRegion } from "@/contexts/RegionContext";
import {
  Globe, Building2, FileText, CreditCard, Save, Loader2,
  CheckCircle2, Info, ExternalLink, Settings2, Plus, Trash2,
  ShieldCheck, Copy, KeyRound, ChevronDown, ChevronUp, Library,
  Users, Mail, X, Lock, AlertCircle, Palette, Sun, Moon, Monitor, RotateCcw,
  CalendarDays, AlertTriangle, RefreshCw, Info as InfoIcon, Eye, Archive,
  Sparkles, Clock, Link2, ClipboardList, BookOpen,
} from "lucide-react";
import { useAppearance } from "@/lib/appearance/provider";
import { SKINS, ACCENTS, FONT_PAIRINGS, PATTERNS } from "@/lib/appearance/catalog";
import { DEFAULT_APPEARANCE } from "@/lib/appearance/types";
import { NCCD_CATEGORIES, NCCD_ADJUSTMENTS, groupByCategory } from "@/lib/nccd-adjustments";
import { useClerk } from "@clerk/react";

type Tab = "profile" | "reports" | "subscription" | "customisation" | "appearance" | "school-year";

const ALL_RECORD_TYPES = [
  { value: "nccd",      label: "Adjustments / NCCD" },
  { value: "behaviour", label: "Behaviour Note" },
  { value: "contact",   label: "Contact Note" },
  { value: "general",   label: "General Note" },
] as const;

function SectionCard({ title, icon: Icon, children }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-5 pb-4 border-b border-gray-100">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
      <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}

// ─── Rollover preflight types + component ────────────────────────────────────

interface RolloverPreflight {
  activeSchoolYear: string | null;
  suggestedOutgoingYear: string | null;
  suggestedNewYear: string | null;
  classes: { activeCount: number; missingSchoolYear: number };
  programRegisters: { activeCount: number; missingSchoolYear: number };
  timetables: { activeCount: number };
  students: { activeCount: number };
  courses: { activeCount: number };
  untouchedTables: string[];
  blocking: {
    missingActiveSchoolYear: boolean;
    classesWithoutSchoolYear: boolean;
    registersWithoutSchoolYear: boolean;
  };
  warnings: {
    noActiveTimetable: boolean;
    diaryExportRecommended: boolean;
  };
}

type CheckStatus = "ok" | "blocking" | "warning" | "info";

function PreflightRow({
  status,
  label,
}: {
  status: CheckStatus;
  label: string;
}) {
  const icon =
    status === "ok" ? (
      <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
    ) : status === "blocking" ? (
      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
    ) : status === "warning" ? (
      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
    ) : (
      <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
    );

  const textClass =
    status === "blocking"
      ? "text-red-700"
      : status === "warning"
        ? "text-amber-700"
        : "text-gray-700";

  return (
    <div className="flex items-start gap-2.5 py-2">
      {icon}
      <span className={`text-sm ${textClass}`}>{label}</span>
    </div>
  );
}

function PreflightSection({
  preflight,
  isLoading,
  onRefresh,
}: {
  preflight: RolloverPreflight | undefined;
  isLoading: boolean;
  onRefresh: () => void;
}) {
  if (isLoading) {
    return (
      <SectionCard title="Year Closeout Readiness" icon={CalendarDays}>
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking readiness…
        </div>
      </SectionCard>
    );
  }

  if (!preflight) return null;

  const { blocking, warnings, classes, programRegisters, timetables, students, courses, untouchedTables, activeSchoolYear, suggestedNewYear } = preflight;
  const isReady =
    !blocking.missingActiveSchoolYear &&
    !blocking.classesWithoutSchoolYear &&
    !blocking.registersWithoutSchoolYear;

  const allRows: { status: CheckStatus; label: string }[] = [
    blocking.missingActiveSchoolYear
      ? {
          status: "blocking",
          label: "No active school year set — add one above before rollover.",
        }
      : {
          status: "ok",
          label: `Active school year is set to "${activeSchoolYear}".${suggestedNewYear ? ` Next year would be "${suggestedNewYear}".` : ""}`,
        },

    blocking.classesWithoutSchoolYear
      ? {
          status: "blocking",
          label: `${classes.missingSchoolYear} of ${classes.activeCount} active ${classes.activeCount === 1 ? "class is" : "classes are"} missing a school year label — label them before rollover.`,
        }
      : {
          status: "ok",
          label: `All ${classes.activeCount} active ${classes.activeCount === 1 ? "class has" : "classes have"} a school year label.`,
        },

    blocking.registersWithoutSchoolYear
      ? {
          status: "blocking",
          label: `${programRegisters.missingSchoolYear} of ${programRegisters.activeCount} active program ${programRegisters.activeCount === 1 ? "register is" : "registers are"} missing a school year label — label them before rollover.`,
        }
      : {
          status: "ok",
          label: `All ${programRegisters.activeCount} active program ${programRegisters.activeCount === 1 ? "register has" : "registers have"} a school year label.`,
        },

    warnings.noActiveTimetable
      ? {
          status: "warning",
          label: "No active diary — consider setting one up or checking archived diaries.",
        }
      : {
          status: "ok",
          label: `${timetables.activeCount} active ${timetables.activeCount === 1 ? "diary" : "diaries"} — ${timetables.activeCount === 1 ? "it" : "they"} will be archived at rollover time.`,
        },

    {
      status: "info",
      label: `${students.activeCount} active ${students.activeCount === 1 ? "student" : "students"} — each will need a keep or archive decision at rollover time.`,
    },

    {
      status: "info",
      label: `${courses.activeCount} active ${courses.activeCount === 1 ? "course" : "courses"} — courses stay active and are not archived at rollover.`,
    },

    warnings.diaryExportRecommended
      ? {
          status: "warning",
          label: "Recommend completing any diary or records exports before rollover.",
        }
      : { status: "ok" as const, label: "" },
  ];
  const rows = allRows.filter((r) => r.label);

  return (
    <SectionCard title="Year Closeout Readiness" icon={CalendarDays}>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            A read-only check of what is ready for year-end closeout.{" "}
            <strong>No data has been changed.</strong>
          </p>
          <button
            onClick={onRefresh}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-gray-700 flex-shrink-0"
            title="Refresh readiness report"
          >
            <RefreshCw className="h-3 w-3" />
            Refresh
          </button>
        </div>

        {isReady && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-100 px-3 py-2 text-sm font-medium text-green-700">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            Ready for rollover — no blocking issues found.
          </div>
        )}
        {!isReady && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm font-medium text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            Not ready — resolve the items marked below before rollover.
          </div>
        )}

        <div className="divide-y rounded-lg border px-3">
          {rows.map((r, i) => (
            <PreflightRow key={i} status={r.status} label={r.label} />
          ))}
        </div>

        <details className="group">
          <summary className="cursor-pointer list-none text-xs text-muted-foreground hover:text-gray-700 flex items-center gap-1">
            <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
            Records never touched by rollover
          </summary>
          <div className="mt-2 pl-4 flex flex-wrap gap-1.5">
            {untouchedTables.map((t) => (
              <span
                key={t}
                className="inline-block rounded-md bg-gray-100 px-2 py-0.5 text-xs font-mono text-gray-600"
              >
                {t}
              </span>
            ))}
          </div>
        </details>
      </div>
    </SectionCard>
  );
}

// ─── Fix unlabelled items ─────────────────────────────────────────────────────

// Minimal local type — the API returns schoolYear even though the generated
// hook type may not expose it; we cast via `unknown` to avoid TS errors.
interface ClassForLabelling {
  id: number;
  name: string;
  yearLevel?: string | null;
  schoolYear?: string | null;
  archived?: boolean;
}

function FixUnlabelledSection({
  activeSchoolYear,
  onFixed,
}: {
  activeSchoolYear: string | null;
  onFixed: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Load active classes — default list excludes archived rows.
  // Cast through unknown to access schoolYear (present at runtime, may be
  // absent from the generated type depending on codegen state).
  const { data: allClassesRaw = [], isLoading: classesLoading } =
    useListClasses();
  const missingClasses = (allClassesRaw as unknown as ClassForLabelling[]).filter(
    (c) => !c.archived && (!c.schoolYear || c.schoolYear.trim() === ""),
  );

  // Load active program registers — useProgramRegisters returns non-archived rows.
  const { data: allRegisters = [], isLoading: registersLoading } =
    useProgramRegisters();
  const missingRegisters = allRegisters.filter(
    (r) => r.status !== "archived" && (!r.schoolYear || r.schoolYear.trim() === ""),
  );

  // Track which items the teacher has explicitly unchecked.
  // All missing items start pre-selected; unchecking adds to these sets.
  const [uncheckedClassIds, setUncheckedClassIds] = useState<Set<number>>(
    new Set(),
  );
  const [uncheckedRegisterIds, setUncheckedRegisterIds] = useState<Set<number>>(
    new Set(),
  );

  const selectedClassIds = missingClasses
    .map((c) => c.id)
    .filter((id) => !uncheckedClassIds.has(id));
  const selectedRegisterIds = missingRegisters
    .map((r) => r.id)
    .filter((id) => !uncheckedRegisterIds.has(id));
  const totalSelected = selectedClassIds.length + selectedRegisterIds.length;

  const applyLabels = useMutation({
    mutationFn: (body: {
      schoolYear: string;
      classIds: number[];
      programRegisterIds: number[];
    }) =>
      customFetch<{ classesUpdated: number; registersUpdated: number }>(
        "/api/school-year/rollover/label-missing",
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: (data) => {
      const total = data.classesUpdated + data.registersUpdated;
      toast({
        title: `School year labels updated — ${total} item${total === 1 ? "" : "s"} labelled. Closeout readiness refreshed.`,
      });
      // Reset selection state so any remaining missing items start pre-checked.
      setUncheckedClassIds(new Set());
      setUncheckedRegisterIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["rollover-preflight"] });
      queryClient.invalidateQueries({ queryKey: getListClassesQueryKey() });
      queryClient.invalidateQueries({ queryKey: programRegistersKey() });
      onFixed();
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't apply labels",
        description: getApiErrorMessage(err, "Try again."),
        variant: "destructive",
      });
    },
  });

  if (!activeSchoolYear) {
    return (
      <SectionCard title="Fix unlabelled items" icon={AlertTriangle}>
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          Set your active school year above before labelling items.
        </p>
      </SectionCard>
    );
  }

  if (classesLoading || registersLoading) {
    return (
      <SectionCard title="Fix unlabelled items" icon={AlertTriangle}>
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      </SectionCard>
    );
  }

  if (missingClasses.length === 0 && missingRegisters.length === 0) {
    return null;
  }

  function toggleClass(id: number, checked: boolean) {
    setUncheckedClassIds((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleRegister(id: number, checked: boolean) {
    setUncheckedRegisterIds((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllClasses(on: boolean) {
    setUncheckedClassIds(on ? new Set() : new Set(missingClasses.map((c) => c.id)));
  }

  function selectAllRegisters(on: boolean) {
    setUncheckedRegisterIds(on ? new Set() : new Set(missingRegisters.map((r) => r.id)));
  }

  return (
    <SectionCard title="Fix unlabelled items" icon={AlertTriangle}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          These active items don't have a school year label yet. Apply{" "}
          <span className="font-semibold">"{activeSchoolYear}"</span> so they
          can be placed in the correct archive box at rollover.
        </p>

        {/* Classes missing school year */}
        {missingClasses.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Classes ({missingClasses.length})
              </span>
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={() =>
                  selectAllClasses(
                    uncheckedClassIds.size > 0 ||
                      selectedClassIds.length < missingClasses.length,
                  )
                }
              >
                {selectedClassIds.length === missingClasses.length
                  ? "Deselect all"
                  : "Select all"}
              </button>
            </div>
            <div className="border rounded-lg divide-y">
              {missingClasses.map((cls) => (
                <label
                  key={cls.id}
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
                >
                  <Checkbox
                    checked={!uncheckedClassIds.has(cls.id)}
                    onCheckedChange={(v) => toggleClass(cls.id, !!v)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{cls.name}</p>
                    {cls.yearLevel && (
                      <p className="text-xs text-muted-foreground">
                        Year {cls.yearLevel}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Program registers missing school year */}
        {missingRegisters.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Program Registers ({missingRegisters.length})
              </span>
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={() =>
                  selectAllRegisters(
                    uncheckedRegisterIds.size > 0 ||
                      selectedRegisterIds.length < missingRegisters.length,
                  )
                }
              >
                {selectedRegisterIds.length === missingRegisters.length
                  ? "Deselect all"
                  : "Select all"}
              </button>
            </div>
            <div className="border rounded-lg divide-y">
              {missingRegisters.map((reg) => {
                const meta = [reg.subject, reg.yearGroup, reg.term]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <label
                    key={reg.id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
                  >
                    <Checkbox
                      checked={!uncheckedRegisterIds.has(reg.id)}
                      onCheckedChange={(v) => toggleRegister(reg.id, !!v)}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{reg.title}</p>
                      {meta && (
                        <p className="text-xs text-muted-foreground truncate">
                          {meta}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {totalSelected === 0
              ? "Select at least one item to apply the label."
              : `${totalSelected} item${totalSelected === 1 ? "" : "s"} selected.`}
          </p>
          <Button
            onClick={() =>
              applyLabels.mutate({
                schoolYear: activeSchoolYear,
                classIds: selectedClassIds,
                programRegisterIds: selectedRegisterIds,
              })
            }
            disabled={totalSelected === 0 || applyLabels.isPending}
            className="gap-2 flex-shrink-0"
            data-testid="button-apply-school-year-labels"
            data-write-action
          >
            {applyLabels.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            Apply "{activeSchoolYear}" to {totalSelected} selected
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Student review ──────────────────────────────────────────────────────────

interface StudentForReview {
  id: number;
  firstName: string;
  lastName: string | null;
  preferredName: string | null;
  yearLevel: string | null;
  schoolId: number | null;
  classNames: string[];
}

type StudentDecision = "keep" | "archive";

function StudentReviewSection({
  onArchiveIdsChange,
}: {
  // Called whenever the set of students marked "archive" changes.
  // Propagates UI-only decisions to the parent so the dry-run preview
  // can use them without any database write.
  onArchiveIdsChange: (ids: number[]) => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["students-for-review"],
    queryFn: () =>
      customFetch<{ students: StudentForReview[] }>(
        "/api/school-year/rollover/students-for-review",
      ),
  });

  const students = data?.students ?? [];

  // UI-only decision state. All students default to "keep".
  // Nothing is written to the database (Slice K onwards).
  const [decisions, setDecisions] = useState<Record<number, StudentDecision>>(
    {},
  );

  function decide(id: number, action: StudentDecision) {
    setDecisions((prev) => ({ ...prev, [id]: action }));
  }

  function getDecision(id: number): StudentDecision {
    return decisions[id] ?? "keep";
  }

  function markAllKeep() {
    const next: Record<number, StudentDecision> = {};
    for (const s of students) next[s.id] = "keep";
    setDecisions(next);
  }

  function markAllArchive() {
    const next: Record<number, StudentDecision> = {};
    for (const s of students) next[s.id] = "archive";
    setDecisions(next);
  }

  const toArchiveCount = students.filter(
    (s) => getDecision(s.id) === "archive",
  ).length;
  const toKeepCount = students.length - toArchiveCount;

  // Notify parent whenever student list or decisions change so DryRunSection
  // always has the latest archive IDs — no DB write, pure state propagation.
  useEffect(() => {
    const ids = students
      .filter((s) => (decisions[s.id] ?? "keep") === "archive")
      .map((s) => s.id);
    onArchiveIdsChange(ids);
  }, [students, decisions, onArchiveIdsChange]);

  return (
    <SectionCard title="Student review" icon={Users}>
      <div className="space-y-4">
        <div className="text-sm text-gray-600 space-y-1">
          <p>
            Students are not archived automatically. During rollover, students
            you leave as <span className="font-medium">Keep active</span> will
            stay available for the new year. Students marked{" "}
            <span className="font-medium">Archive at rollover</span> can be
            restored any time from Past students.
          </p>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading students…
          </div>
        )}

        {isError && (
          <p className="text-sm text-destructive">
            Couldn't load students. Refresh to try again.
          </p>
        )}

        {!isLoading && !isError && students.length === 0 && (
          <p className="text-sm text-muted-foreground py-2">
            No active students found.
          </p>
        )}

        {students.length > 0 && (
          <>
            {/* Bulk toggles */}
            <div className="flex items-center gap-3 text-xs">
              <span className="text-muted-foreground">All students:</span>
              <button
                className="text-blue-600 hover:underline"
                onClick={markAllKeep}
              >
                Keep all
              </button>
              <button
                className="text-amber-600 hover:underline"
                onClick={markAllArchive}
              >
                Archive all at rollover
              </button>
            </div>

            {/* Student list */}
            <div className="border rounded-lg divide-y">
              {students.map((s) => {
                const decision = getDecision(s.id);
                const displayName = [
                  s.lastName ? `${s.lastName},` : null,
                  s.preferredName
                    ? `${s.firstName} (${s.preferredName})`
                    : s.firstName,
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <div
                    key={s.id}
                    className={`flex items-start gap-3 px-3 py-2.5 transition-colors ${
                      decision === "archive" ? "bg-amber-50/60" : ""
                    }`}
                  >
                    {/* Student info */}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {displayName}
                      </p>
                      <div className="flex flex-wrap gap-x-2 mt-0.5">
                        {s.yearLevel && (
                          <span className="text-xs text-muted-foreground">
                            Year {s.yearLevel}
                          </span>
                        )}
                        {s.classNames.length > 0 && (
                          <span className="text-xs text-muted-foreground truncate">
                            {s.classNames.join(", ")}
                          </span>
                        )}
                        {s.classNames.length === 0 && (
                          <span className="text-xs text-muted-foreground italic">
                            No class
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Decision toggle */}
                    <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                      <button
                        onClick={() => decide(s.id, "keep")}
                        className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                          decision === "keep"
                            ? "bg-green-100 border-green-300 text-green-800"
                            : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                        data-testid={`student-keep-${s.id}`}
                      >
                        Keep active
                      </button>
                      <button
                        onClick={() => decide(s.id, "archive")}
                        className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                          decision === "archive"
                            ? "bg-amber-100 border-amber-300 text-amber-800"
                            : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                        data-testid={`student-archive-${s.id}`}
                      >
                        Archive at rollover
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
              <span>
                {toKeepCount} keep active · {toArchiveCount} archive at rollover
              </span>
            </div>

            {/* Future-action placeholder */}
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 text-xs text-muted-foreground">
              <InfoIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              These choices will be applied during the final rollover step.
              Nothing has been archived yet.
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}

// ─── Rollover dry-run preview ─────────────────────────────────────────────────

interface DryRunResult {
  ready: boolean;
  blockingIssues: Array<{ code: string; message: string }>;
  warnings: Array<{ code: string; message: string }>;
  outgoingYear: string;
  newYear: string;
  wouldArchive: {
    classes: Array<{
      id: number;
      name: string;
      yearLevel: string | null;
      schoolYear: string | null;
    }>;
    timetables: Array<{ id: number; name: string }>;
    programRegisters: Array<{
      id: number;
      title: string;
      status: string;
      schoolYear: string | null;
    }>;
    students: Array<{
      id: number;
      firstName: string;
      lastName: string | null;
      yearLevel: string | null;
    }>;
  };
  wouldKeep: {
    students: Array<{
      id: number;
      firstName: string;
      lastName: string | null;
      yearLevel: string | null;
    }>;
    courses: Array<{ id: number; name: string }>;
  };
  wouldNotTouch: string[];
  counts: {
    classesWouldArchive: number;
    timetablesWouldArchive: number;
    programRegistersWouldArchive: number;
    studentsWouldArchive: number;
    studentsWouldKeep: number;
    coursesWouldKeep: number;
  };
}

const UNTOUCHED_TABLE_LABELS: Record<string, string> = {
  diary_entries: "Diary / Daybook entries",
  records: "Student observation records",
  student_classes: "Student–class membership links",
  program_items: "Program items",
  program_delivery_events: "Program delivery events",
  diary_program_links: "Diary–program links",
  program_evaluations: "Program evaluations",
};

function PreviewGroup({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
        {title} ({count})
      </p>
      {children}
    </div>
  );
}

// ─── Post-rollover New Year Setup checklist ──────────────────────────────────

function NewYearSetupChecklist({
  preflight,
  justRolledOver,
}: {
  preflight: RolloverPreflight | undefined;
  justRolledOver: boolean;
}) {
  if (!preflight) return null;

  const activeSchoolYear = preflight.activeSchoolYear;

  if (!activeSchoolYear) return null;

  const hasSparseSetup =
    preflight != null &&
    (preflight.timetables.activeCount === 0 ||
      preflight.classes.activeCount === 0 ||
      preflight.programRegisters.activeCount === 0 ||
      preflight.students.activeCount === 0);

  if (!justRolledOver && !hasSparseSetup) return null;

  type Step = {
    icon: React.ElementType;
    title: string;
    desc: string;
    done: boolean;
    href: string | null;
    linkLabel: string | null;
    informational?: boolean;
  };

  const steps: Step[] = [
    {
      icon: CalendarDays,
      title: "Confirm active school year",
      desc: `Your active school year is set to "${activeSchoolYear}".`,
      done: true,
      href: null,
      linkLabel: null,
    },
    {
      icon: Clock,
      title: "Set up timetable",
      desc: "Create a new timetable for the year.",
      done: preflight.timetables.activeCount > 0,
      href: "/app/timetable-settings",
      linkLabel: "Go to Timetable",
    },
    {
      icon: Library,
      title: "Create or import classes",
      desc: "Add the classes you'll be teaching this year.",
      done: preflight.classes.activeCount > 0,
      href: "/app/classes",
      linkLabel: "Go to Classes",
    },
    {
      icon: Users,
      title: "Add students",
      desc: "Add new students, or open Past Students to restore any you archived by mistake.",
      done: preflight.students.activeCount > 0,
      href: "/app/students",
      linkLabel: "Go to Students",
    },
    {
      icon: Link2,
      title: "Link students to classes",
      desc: "Open a class and use the Students tab to add members.",
      done: false,
      href: "/app/classes",
      linkLabel: "Go to Classes",
      informational: true,
    },
    {
      icon: ClipboardList,
      title: "Create or copy program registers",
      desc: "Set up your program documentation for the new year.",
      done: preflight.programRegisters.activeCount > 0,
      href: "/app/program-registration",
      linkLabel: "Go to Programs",
    },
    {
      icon: BookOpen,
      title: "Start using diary",
      desc: "Begin recording lessons and observations for the new year.",
      done: false,
      href: "/app/diary",
      linkLabel: "Go to Diary",
      informational: true,
    },
  ];

  const trackable = steps.filter((s) => !s.informational);
  const completedCount = trackable.filter((s) => s.done).length;

  return (
    <SectionCard title="New Year Setup" icon={Sparkles}>
      <div className="space-y-4">
        <div className="flex items-start gap-2 bg-teal-50 border border-teal-100 rounded-lg p-3 text-sm text-teal-800">
          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-teal-600" />
          <span>
            {justRolledOver ? (
              <>
                Your old year is safely archived. Now set up the workspace for{" "}
                <span className="font-semibold">{activeSchoolYear}</span>.
              </>
            ) : (
              <>
                Set up your workspace for{" "}
                <span className="font-semibold">{activeSchoolYear}</span>.
              </>
            )}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-500 rounded-full transition-all duration-500"
              style={{ width: `${(completedCount / trackable.length) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {completedCount} of {trackable.length} complete
          </span>
        </div>

        <ol className="space-y-2">
          {steps.map((step, idx) => {
            const StepIcon = step.icon;
            return (
              <li
                key={idx}
                className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 text-sm transition-colors ${
                  step.done
                    ? "border-teal-100 bg-teal-50/50"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div
                  className={`mt-0.5 h-5 w-5 flex-shrink-0 rounded-full flex items-center justify-center ${
                    step.done
                      ? "bg-teal-100 text-teal-600"
                      : step.informational
                      ? "bg-gray-100 text-gray-400"
                      : "bg-orange-50 text-orange-500"
                  }`}
                >
                  {step.done ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <StepIcon className="h-3 w-3" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`font-medium ${
                        step.done ? "text-teal-700" : "text-gray-800"
                      }`}
                    >
                      {idx + 1}. {step.title}
                    </span>
                    {step.informational && (
                      <span className="text-xs text-gray-400 font-normal">
                        (informational)
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                  {step.href && !step.done && (
                    <a
                      href={step.href}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline mt-1"
                    >
                      {step.linkLabel}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </SectionCard>
  );
}

// ─── Dry-run + execute ────────────────────────────────────────────────────────

function DryRunSection({
  activeSchoolYear,
  studentArchiveIds,
  onRolloverSuccess,
}: {
  activeSchoolYear: string | null;
  studentArchiveIds: number[];
  onRolloverSuccess?: () => void;
}) {
  const { toast } = useToast();

  const suggestedNewYear = /^\d{4}$/.test(activeSchoolYear?.trim() ?? "")
    ? String(parseInt(activeSchoolYear!.trim(), 10) + 1)
    : "";
  const [newYear, setNewYear] = useState(suggestedNewYear);

  // Keep newYear in sync if activeSchoolYear is updated above.
  useEffect(() => {
    if (suggestedNewYear) setNewYear(suggestedNewYear);
  }, [suggestedNewYear]);

  const dryRun = useMutation({
    mutationFn: () =>
      customFetch<DryRunResult>("/api/school-year/rollover/dry-run", {
        method: "POST",
        body: JSON.stringify({
          outgoingYear: activeSchoolYear?.trim() ?? "",
          newYear: newYear.trim(),
          studentArchiveIds,
        }),
      }),
    onError: (err: unknown) => {
      toast({
        title: "Couldn't run preview",
        description: getApiErrorMessage(err, "Try again."),
        variant: "destructive",
      });
    },
  });

  const result = dryRun.data;

  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executeSuccess, setExecuteSuccess] = useState<{
    archivedYear: string;
    newYear: string;
  } | null>(null);
  const [executeBlockingIssues, setExecuteBlockingIssues] = useState<
    Array<{ code: string; message: string }>
  >([]);

  const execute = useMutation({
    mutationFn: (body: {
      outgoingYear: string;
      newYear: string;
      studentArchiveIds: number[];
    }) =>
      customFetch<{ success: boolean; summary: Record<string, number> }>(
        "/api/school-year/rollover/execute",
        { method: "POST", body: JSON.stringify(body) }
      ),
    onSuccess: (_, vars) => {
      setExecuteSuccess({ archivedYear: vars.outgoingYear, newYear: vars.newYear });
      setExecuteBlockingIssues([]);
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["rollover-preflight"] });
      queryClient.invalidateQueries({ queryKey: ["archive-years"] });
      queryClient.invalidateQueries({ queryKey: getListClassesQueryKey() });
      queryClient.invalidateQueries({ queryKey: programRegistersKey() });
      queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
      queryClient.invalidateQueries({ queryKey: ["students-for-review"] });
      onRolloverSuccess?.();
    },
    onError: (err: unknown) => {
      setConfirmOpen(false);
      const data =
        err instanceof ApiError && err.data && typeof err.data === "object"
          ? (err.data as Record<string, unknown>)
          : null;
      const issues = Array.isArray(data?.blockingIssues)
        ? (data!.blockingIssues as Array<{ code: string; message: string }>)
        : [];
      setExecuteBlockingIssues(issues);
      if (issues.length === 0) {
        toast({
          title: "Couldn't close out year",
          description: getApiErrorMessage(err, "Refresh the preview and try again."),
          variant: "destructive",
        });
      }
    },
  });

  function studentLabel(s: {
    firstName: string;
    lastName: string | null;
    yearLevel: string | null;
  }) {
    const name = [s.lastName ? `${s.lastName},` : null, s.firstName]
      .filter(Boolean)
      .join(" ");
    return s.yearLevel ? `${name} (Yr ${s.yearLevel})` : name;
  }

  return (
    <SectionCard title="Preview closeout impact" icon={Eye}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Review exactly what would happen when you close out the year. This is
          a preview only — nothing will be changed until you confirm at the
          bottom of this section.
        </p>

        {/* Year summary + trigger */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-gray-500">Outgoing year:</span>
            <span className="font-semibold">{activeSchoolYear ?? "—"}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-gray-500">New year:</span>
            {suggestedNewYear ? (
              <span className="font-semibold">{newYear}</span>
            ) : (
              <Input
                value={newYear}
                onChange={(e) => setNewYear(e.target.value)}
                placeholder="e.g. 2027"
                className="h-7 w-24 text-sm"
              />
            )}
          </div>
          <Button
            size="sm"
            onClick={() => dryRun.mutate()}
            disabled={!activeSchoolYear || !newYear.trim() || dryRun.isPending}
            className="gap-1.5"
            data-testid="button-preview-closeout"
          >
            {dryRun.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )}
            Preview closeout
          </Button>
          {dryRun.isSuccess && !dryRun.isPending && (
            <span className="text-xs text-muted-foreground">
              {studentArchiveIds.length} student
              {studentArchiveIds.length === 1 ? "" : "s"} marked to archive
            </span>
          )}
        </div>

        {/* Results */}
        {result && !executeSuccess && (
          <div className="space-y-4 pt-2 border-t">
            {/* Ready / not-ready banner */}
            <div
              className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium ${
                result.ready
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}
            >
              {result.ready ? (
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              )}
              {result.ready
                ? `Ready to close out ${result.outgoingYear} — no blocking issues found.`
                : `Not ready — ${result.blockingIssues.length} blocking issue${result.blockingIssues.length === 1 ? "" : "s"} to resolve first.`}
            </div>

            {/* Blocking issues */}
            {result.blockingIssues.length > 0 && (
              <div className="space-y-1.5">
                {result.blockingIssues.map((issue) => (
                  <div
                    key={issue.code}
                    className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                  >
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    {issue.message}
                  </div>
                ))}
              </div>
            )}

            {/* Warnings */}
            {result.warnings.length > 0 && (
              <div className="space-y-1.5">
                {result.warnings.map((w) => (
                  <div
                    key={w.code}
                    className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2"
                  >
                    <InfoIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    {w.message}
                  </div>
                ))}
              </div>
            )}

            {/* Would be archived */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                These items would be placed into the {result.outgoingYear}{" "}
                archive
              </p>

              {result.counts.classesWouldArchive > 0 ? (
                <PreviewGroup
                  title="Classes"
                  count={result.counts.classesWouldArchive}
                >
                  <div className="border rounded-lg divide-y">
                    {result.wouldArchive.classes.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700"
                      >
                        <span className="flex-1 truncate">{c.name}</span>
                        {c.yearLevel && (
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            Yr {c.yearLevel}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </PreviewGroup>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No classes labelled "{result.outgoingYear}" — none would be
                  archived.
                </p>
              )}

              {result.counts.timetablesWouldArchive > 0 && (
                <PreviewGroup
                  title="Timetables"
                  count={result.counts.timetablesWouldArchive}
                >
                  <div className="border rounded-lg divide-y">
                    {result.wouldArchive.timetables.map((t) => (
                      <div
                        key={t.id}
                        className="px-3 py-1.5 text-sm text-gray-700"
                      >
                        {t.name}
                      </div>
                    ))}
                  </div>
                </PreviewGroup>
              )}

              {result.counts.programRegistersWouldArchive > 0 ? (
                <PreviewGroup
                  title="Program registers"
                  count={result.counts.programRegistersWouldArchive}
                >
                  <div className="border rounded-lg divide-y">
                    {result.wouldArchive.programRegisters.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700"
                      >
                        <span className="flex-1 truncate">{r.title}</span>
                        <span className="text-xs text-muted-foreground capitalize flex-shrink-0">
                          {r.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </PreviewGroup>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No program registers labelled "{result.outgoingYear}" — none
                  would be archived.
                </p>
              )}

              {result.counts.studentsWouldArchive > 0 ? (
                <PreviewGroup
                  title="Students (moving to Past students)"
                  count={result.counts.studentsWouldArchive}
                >
                  <div className="border rounded-lg divide-y">
                    {result.wouldArchive.students.map((s) => (
                      <div
                        key={s.id}
                        className="px-3 py-1.5 text-sm text-gray-700"
                      >
                        {studentLabel(s)}
                      </div>
                    ))}
                  </div>
                </PreviewGroup>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No students selected for archiving — all would remain active.
                </p>
              )}
            </div>

            {/* Would stay active */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                These items would stay active for {result.newYear}
              </p>

              {result.counts.studentsWouldKeep > 0 && (
                <PreviewGroup
                  title="Students (staying active)"
                  count={result.counts.studentsWouldKeep}
                >
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                    {result.wouldKeep.students.map((s) => (
                      <div
                        key={s.id}
                        className="px-3 py-1.5 text-sm text-gray-700"
                      >
                        {studentLabel(s)}
                      </div>
                    ))}
                  </div>
                </PreviewGroup>
              )}

              {result.counts.coursesWouldKeep > 0 && (
                <PreviewGroup
                  title="Courses (always stay active)"
                  count={result.counts.coursesWouldKeep}
                >
                  <div className="border rounded-lg divide-y">
                    {result.wouldKeep.courses.map((c) => (
                      <div
                        key={c.id}
                        className="px-3 py-1.5 text-sm text-gray-700"
                      >
                        {c.name}
                      </div>
                    ))}
                  </div>
                </PreviewGroup>
              )}
            </div>

            {/* Would not touch */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                These records will not be changed
              </p>
              <div className="border rounded-lg divide-y">
                {result.wouldNotTouch.map((tbl) => (
                  <div
                    key={tbl}
                    className="flex items-center gap-2 px-3 py-1.5"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-gray-600">
                      {UNTOUCHED_TABLE_LABELS[tbl] ?? tbl}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 text-xs text-muted-foreground">
              <InfoIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              This is a preview only. No data has been changed.
            </div>

            {/* Execute blocking-issue errors returned from the server */}
            {executeBlockingIssues.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-red-600">
                  The server found new blocking issues — refresh the preview and resolve them before trying again.
                </p>
                <div className="space-y-1.5">
                  {executeBlockingIssues.map((issue) => (
                    <div
                      key={issue.code}
                      className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                    >
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      {issue.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Final close-out action — only enabled when preview is ready */}
            {result.ready && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 space-y-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-orange-900">
                    Ready to close out {result.outgoingYear}
                  </p>
                  <p className="text-xs text-orange-700">
                    This will archive the items listed above and set your active school year to{" "}
                    <span className="font-medium">{result.newYear}</span>. This action cannot be undone from within ClassTrack.
                  </p>
                </div>
                <Button
                  size="sm"
                  className="gap-1.5 bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => setConfirmOpen(true)}
                  disabled={execute.isPending}
                  data-testid="button-closeout-year"
                >
                  {execute.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Archive className="h-3.5 w-3.5" />
                  )}
                  Close out {result.outgoingYear} and start {result.newYear}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Execute success */}
        {executeSuccess && (
          <div className="space-y-4 pt-2 border-t">
            <div className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium bg-green-50 border border-green-200 text-green-800">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              {executeSuccess.archivedYear} has been archived. Your active school year is now{" "}
              {executeSuccess.newYear}.
            </div>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Next step: set up your {executeSuccess.newYear} timetable, classes and programs.
              </p>
              <a
                href="/app/archive"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                <Archive className="h-3.5 w-3.5" />
                Open {executeSuccess.archivedYear} Archive box
              </a>
            </div>
          </div>
        )}

        {/* Confirmation dialog */}
        {result && !executeSuccess && (
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Close out {result.outgoingYear} and start {result.newYear}?
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm">
                    <p>
                      You are about to close out{" "}
                      <span className="font-medium">{result.outgoingYear}</span> and start{" "}
                      <span className="font-medium">{result.newYear}</span>.
                    </p>
                    <div>
                      <p className="font-medium text-gray-800 mb-1">ClassTrack will archive:</p>
                      <ul className="space-y-0.5 text-gray-600 ml-3 list-disc list-inside">
                        <li>
                          {result.counts.classesWouldArchive}{" "}
                          {result.counts.classesWouldArchive === 1 ? "class" : "classes"}
                        </li>
                        <li>
                          {result.counts.timetablesWouldArchive}{" "}
                          {result.counts.timetablesWouldArchive === 1 ? "timetable" : "timetables"}
                        </li>
                        <li>
                          {result.counts.programRegistersWouldArchive} program{" "}
                          {result.counts.programRegistersWouldArchive === 1
                            ? "register"
                            : "registers"}
                        </li>
                        <li>
                          {result.counts.studentsWouldArchive} selected{" "}
                          {result.counts.studentsWouldArchive === 1 ? "student" : "students"}
                        </li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 mb-1">ClassTrack will keep active:</p>
                      <ul className="space-y-0.5 text-gray-600 ml-3 list-disc list-inside">
                        <li>
                          {result.counts.studentsWouldKeep}{" "}
                          {result.counts.studentsWouldKeep === 1 ? "student" : "students"}
                        </li>
                        <li>
                          {result.counts.coursesWouldKeep}{" "}
                          {result.counts.coursesWouldKeep === 1 ? "course" : "courses"}
                        </li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 mb-1">
                        ClassTrack will not change:
                      </p>
                      <ul className="space-y-0.5 text-gray-600 ml-3 list-disc list-inside">
                        <li>Diary entries</li>
                        <li>Records</li>
                        <li>Student–class links</li>
                        <li>Program items</li>
                        <li>Program delivery events</li>
                        <li>Diary–program links</li>
                        <li>Program evaluations</li>
                      </ul>
                    </div>
                    <p className="text-gray-700">
                      This action will move {result.outgoingYear} into your Archive box.
                      Continue?
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={execute.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={execute.isPending}
                  className="bg-orange-600 hover:bg-orange-700"
                  onClick={() =>
                    execute.mutate({
                      outgoingYear: result.outgoingYear,
                      newYear: result.newYear,
                      studentArchiveIds,
                    })
                  }
                >
                  {execute.isPending && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  )}
                  Yes, close out {result.outgoingYear}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </SectionCard>
  );
}

// ─── Head-teacher PIN card ───────────────────────────────────────────────────
// The plain-text PIN is never stored on the server (Phase 3A — hashed at
// rest). To share a PIN with new joiners the head teacher mints a fresh
// one here; the value is shown ONCE and is unrecoverable after refresh.
function HeadTeacherPinCard({
  currentHeadTeacherPin,
  onRegenerated,
}: {
  currentHeadTeacherPin: string | null;
  onRegenerated: (pin: string) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pin, setPin] = useState<string | null>(currentHeadTeacherPin);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const regenerate = useMutation({
    mutationFn: () =>
      customFetch<{ headTeacherPin: string }>("/api/school/regenerate-pin", {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: (data) => {
      setPin(data.headTeacherPin);
      onRegenerated(data.headTeacherPin);
      setConfirming(false);
      // Invalidate cached profile + school-access so any other component
      // reading headTeacherPinSet refreshes (e.g. setup gate, banners).
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/school/access"] });
      toast({
        title: "New Head Teacher PIN generated",
        description: "Copy it now — it won't be shown again after you leave this page.",
      });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to generate a new PIN",
        description: getApiErrorMessage(err, "Try again."),
        variant: "destructive",
      });
    },
  });

  const handleCopy = () => {
    if (!pin) return;
    navigator.clipboard.writeText(pin);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-xl border border-teal-200 bg-teal-50 p-4 space-y-2">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-teal-600" />
        <p className="text-sm font-semibold text-teal-800">Your Head Teacher PIN</p>
      </div>
      <p className="text-xs text-teal-700">
        Share this with other head teachers at your school so they can verify themselves and access School Overview. PINs are stored securely — we can't show you the existing one, so generate a new one when you need to share.
      </p>

      {pin ? (
        <div className="flex items-center gap-2 mt-1">
          <code className="flex-1 text-center text-lg font-bold text-teal-900 tracking-widest bg-white border border-teal-200 rounded-lg py-2 px-4">
            {pin}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="gap-1 border-teal-300 text-teal-700 hover:bg-teal-100 shrink-0"
            data-testid="button-copy-head-teacher-pin"
          >
            {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-teal-700 italic">No PIN visible. Generate a new one to share.</p>
      )}

      <div className="pt-1">
        {confirming ? (
          <div className="space-y-2">
            <p className="text-xs text-teal-800">
              Generating a new PIN immediately deactivates the old one. Anyone holding the previous PIN won't be able to use it. Continue?
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => regenerate.mutate()}
                disabled={regenerate.isPending}
                data-testid="button-confirm-regenerate-pin"
              >
                {regenerate.isPending ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
                ) : "Yes, generate new PIN"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirming(false)}
                disabled={regenerate.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirming(true)}
            className="gap-1 border-teal-300 text-teal-700 hover:bg-teal-100"
            data-testid="button-regenerate-head-teacher-pin"
          >
            <KeyRound className="h-3.5 w-3.5" />
            {pin ? "Generate a new PIN" : "Generate PIN"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── School access card (Task #44) ────────────────────────────────────────────
// Head-teacher self-service for the join controls on their school's code:
// allowed email domains, the lock toggle, and a read-only summary of seats /
// status / mailto link to request more seats. Seat caps and revocation are
// admin-only — that's intentional.

type SchoolAccessData = {
  schoolCode: string;
  displayName: string | null;
  headTeacherPin: string | null;
  seatLimit: number | null;
  seatsUsed: number;
  allowedEmailDomains: string[];
  domainLockEnabled: boolean;
  status: "active" | "revoked" | string;
};

function SchoolAccessCard({ dismissedBanner }: { dismissedBanner: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [bannerOpen, setBannerOpen] = useState(!dismissedBanner);
  const [newDomain, setNewDomain] = useState("");

  const { data, isLoading, isError } = useQuery<SchoolAccessData>({
    queryKey: ["/api/school/access"],
    queryFn: () => customFetch<SchoolAccessData>("/api/school/access"),
  });

  const dismissBanner = useMutation({
    mutationFn: () =>
      customFetch("/api/profile", {
        method: "PUT",
        body: JSON.stringify({ dismissedSchoolControlsBanner: true }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
    },
  });

  const updateAccess = useMutation({
    mutationFn: (patch: { allowedEmailDomains?: string[]; domainLockEnabled?: boolean }) =>
      customFetch<SchoolAccessData>("/api/school/access", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/school/access"] });
      toast({ title: "School access updated" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Update failed",
        description: getApiErrorMessage(err, "Could not update school access."),
        variant: "destructive",
      });
    },
  });

  const handleAddDomain = () => {
    const cleaned = newDomain.trim().toLowerCase().replace(/^@/, "");
    if (!cleaned) return;
    if (data?.allowedEmailDomains.includes(cleaned)) {
      setNewDomain("");
      return;
    }
    updateAccess.mutate({ allowedEmailDomains: [...(data?.allowedEmailDomains ?? []), cleaned] });
    setNewDomain("");
  };

  const handleRemoveDomain = (domain: string) => {
    if (!data) return;
    updateAccess.mutate({
      allowedEmailDomains: data.allowedEmailDomains.filter((d) => d !== domain),
    });
  };

  const handleToggleLock = (next: boolean) => {
    updateAccess.mutate({ domainLockEnabled: next });
  };

  const handleDismissBanner = () => {
    setBannerOpen(false);
    dismissBanner.mutate();
  };

  if (isLoading) {
    return (
      <SectionCard title="School Access" icon={ShieldCheck}>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
        </div>
      </SectionCard>
    );
  }

  if (isError || !data) {
    return (
      <SectionCard title="School Access" icon={ShieldCheck}>
        <p className="text-sm text-gray-500">Could not load school access controls.</p>
      </SectionCard>
    );
  }

  const seatPct = data.seatLimit ? Math.min(100, Math.round((data.seatsUsed / data.seatLimit) * 100)) : 0;
  const isFull = data.seatLimit !== null && data.seatsUsed >= data.seatLimit;
  const isNearFull = data.seatLimit !== null && !isFull && data.seatsUsed >= Math.floor(data.seatLimit * 0.8);

  return (
    <>
      {bannerOpen && (
        <div className="rounded-xl border border-teal-200 bg-teal-50 p-4 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-teal-100 text-teal-700 shrink-0">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-teal-900">New: school join controls</p>
            <p className="text-xs text-teal-800 mt-1 leading-relaxed">
              You can now lock your school's join code to specific email domains and see who's used a seat. Seat
              limits and revoking a code are still handled by the ClassTrack team — email us anytime if you need
              changes.
            </p>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={handleDismissBanner}
            className="text-teal-600 hover:text-teal-900 shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <SectionCard title="School Access" icon={ShieldCheck}>
        <div className="space-y-5">
          {data.status === "revoked" && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
              <div className="text-xs text-rose-800">
                <p className="font-semibold">This school code is revoked.</p>
                <p className="mt-0.5">Existing teachers keep their access, but no new joins are accepted. Contact ClassTrack to reactivate.</p>
              </div>
            </div>
          )}

          {/* Seats */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-gray-400" />
                Seats used
              </Label>
              <span className={`text-sm font-semibold ${isFull ? "text-rose-700" : isNearFull ? "text-amber-700" : "text-gray-700"}`}>
                {data.seatsUsed}{data.seatLimit !== null && <span className="text-gray-400 font-normal"> / {data.seatLimit}</span>}
              </span>
            </div>
            {data.seatLimit !== null && (
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${isFull ? "bg-rose-500" : isNearFull ? "bg-amber-500" : "bg-teal-500"}`}
                  style={{ width: `${seatPct}%` }}
                />
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Need more seats?{" "}
              <a
                href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL ?? "classtrack.com.au@gmail.com"}?subject=More%20seats%20for%20${encodeURIComponent(data.displayName ?? data.schoolCode)}%20(${data.schoolCode})&body=Hi%20ClassTrack%2C%0A%0AOur%20school%20${encodeURIComponent(data.displayName ?? data.schoolCode)}%20(code%20${data.schoolCode})%20needs%20more%20seats.%20We%20currently%20have%20${data.seatsUsed}%20of%20${data.seatLimit ?? "—"}%20used.%20Please%20increase%20our%20cap%20to%20...%0A%0AThanks!`}
                className="text-primary underline"
              >
                Email ClassTrack
              </a>
              {" "}— we usually respond within a business day.
            </p>
          </div>

          {/* Domain lock toggle */}
          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="lock-toggle" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5 text-gray-400" />
                  Lock to school email domains
                </Label>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  When on, only people whose verified Clerk email matches one of the domains below can join
                  using your school code.
                </p>
              </div>
              <Switch
                id="lock-toggle"
                checked={data.domainLockEnabled}
                disabled={updateAccess.isPending}
                onCheckedChange={handleToggleLock}
              />
            </div>
          </div>

          {/* Domain list */}
          <div>
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-gray-400" />
              Allowed email domains
            </Label>
            <p className="text-xs text-gray-500 mt-1 mb-2">
              One per line. Use <code className="font-mono text-[11px] bg-gray-100 px-1 rounded">*.example.edu.au</code> to
              also allow subdomains.
            </p>
            <div className="space-y-1.5 mb-2">
              {data.allowedEmailDomains.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No domains added yet.</p>
              ) : (
                data.allowedEmailDomains.map((d) => (
                  <div key={d} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg border border-gray-100 bg-gray-50">
                    <code className="text-sm font-mono text-gray-800">{d}</code>
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(d)}
                      disabled={updateAccess.isPending}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. riverside.nsw.edu.au"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddDomain())}
                className="font-mono text-sm flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!newDomain.trim() || updateAccess.isPending}
                onClick={handleAddDomain}
                className="gap-1 shrink-0"
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
        </div>
      </SectionCard>
    </>
  );
}

export default function Settings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: profile, isLoading } = useGetProfile();

  // UI-only: student archive decisions lifted from StudentReviewSection so
  // DryRunSection can read the current selection without any DB interaction.
  const [studentArchiveIds, setStudentArchiveIds] = useState<number[]>([]);
  const { data: tierData } = useSubscriptionTier();
  const aiUsedThisMonth = tierData?.aiUsed ?? 0;
  const aiMonthlyLimit = (tierData as any)?.aiMonthlyLimit ?? 300;
  const aiSoftLimitReached = (tierData as any)?.aiSoftLimitReached ?? false;
  const aiMonthlyLimitEnforced = (tierData as any)?.aiMonthlyLimitEnforced ?? false;
  const createCheckout = useCreateCheckout();
  const openPortal = useOpenPortal();
  const { terms } = useRegion();

  const handleSubscribe = async () => {
    try {
      const result = await createCheckout.mutateAsync(getProPriceId());
      if (result.url) window.location.href = result.url;
    } catch (err) {
      toast({
        title: "Could not start checkout",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleManageBilling = async () => {
    try {
      const result = await openPortal.mutateAsync();
      if (result.url) window.location.href = result.url;
    } catch (err) {
      toast({
        title: "Could not open billing portal",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const p = new URLSearchParams(window.location.search).get("tab");
    const valid: Tab[] = ["profile", "reports", "subscription", "customisation", "appearance", "school-year"];
    return valid.includes(p as Tab) ? (p as Tab) : "profile";
  });
  const [saved, setSaved] = useState(false);

  const [yearForm, setYearForm] = useState({ activeSchoolYear: "" });
  const [yearSaved, setYearSaved] = useState(false);
  const [justRolledOver, setJustRolledOver] = useState(false);

  const [prefs, setPrefs] = useState({
    enabledRecordTypes: ["nccd", "behaviour", "contact", "general"] as string[],
    defaultRecordType: "general" as string,
    customAdjustments: [] as { id: string; label: string; category: string }[],
    hiddenAdjustmentIds: [] as string[],
    renamedAdjustments: {} as Record<string, string>,
    // Phase 2 (Task #56) — account-wide default for how a student's name
    // is rendered across the app. Per-student `displayMode` overrides
    // this setting. Stored in /api/profile preferences (freeform jsonb).
    defaultStudentDisplayMode: "full" as "full" | "first" | "initials" | "nickname" | "code",
  });
  const [newAdj, setNewAdj] = useState({ label: "", category: "Instructional" });
  const [openStandardCats, setOpenStandardCats] = useState<Set<string>>(new Set());
  const [prefsSaved, setPrefsSaved] = useState(false);

  const [form, setForm] = useState({
    schoolName: "",
    region: "au" as Region,
    teachingLevel: "",
    reportHeader: "",
    reportFooter: "",
    dateFormat: "dd/MM/yyyy",
    schoolCode: "",
    headTeacherPin: "",
  });
  const [currentHeadTeacherPin, setCurrentHeadTeacherPin] = useState<string | null>(null);
  const [currentSchoolRole, setCurrentSchoolRole] = useState<"teacher" | "head_teacher" | "support_staff">("teacher");
  const [pinCopied, setPinCopied] = useState(false);
  // Task #58 — the school-join acknowledgement is a per-form-attempt
  // tick. The user must explicitly confirm any school-code change. We
  // reset this whenever they edit the schoolCode field again, so a
  // tick can never carry across to a different code.
  const [schoolJoinAckTicked, setSchoolJoinAckTicked] = useState(false);
  // Only seed `form` from `profile` ONCE per session. React Query refetches
  // (e.g. on window focus) would otherwise overwrite anything the user has
  // typed but not yet saved — including the school code, which silently
  // bypassed the join-enforcement in PUT /api/profile by sending "".
  const formSeededRef = useRef(false);

  useEffect(() => {
    if (profile) {
      const p = profile as any;
      // Always keep the displayed role/PIN in sync with the latest server
      // truth (these are read-only chips, not user-editable fields).
      setCurrentSchoolRole(p.schoolRole ?? "teacher");
      setCurrentHeadTeacherPin(p.headTeacherPin ?? null);

      if (!formSeededRef.current) {
        formSeededRef.current = true;
        setForm({
          schoolName: p.schoolName ?? "",
          region: (p.region ?? "au") as Region,
          teachingLevel: p.teachingLevel ?? "",
          reportHeader: p.reportHeader ?? "",
          reportFooter: p.reportFooter ?? "",
          dateFormat: p.dateFormat ?? "dd/MM/yyyy",
          schoolCode: p.schoolCode ?? "",
          headTeacherPin: "",
        });
        setYearForm({ activeSchoolYear: p.activeSchoolYear ?? "" });
        if (p.preferences) {
          setPrefs({
            enabledRecordTypes: p.preferences.enabledRecordTypes ?? ["nccd", "behaviour", "contact", "general"],
            defaultRecordType: p.preferences.defaultRecordType ?? "general",
            customAdjustments: p.preferences.customAdjustments ?? [],
            hiddenAdjustmentIds: p.preferences.hiddenAdjustmentIds ?? [],
            renamedAdjustments: p.preferences.renamedAdjustments ?? {},
            defaultStudentDisplayMode: p.preferences.defaultStudentDisplayMode ?? "full",
          });
        }
      }
    }
  }, [profile]);

  const savePrefsApi = (data: typeof prefs) =>
    customFetch("/api/profile", {
      method: "PUT",
      body: JSON.stringify({ preferences: data }),
    });

  const savePrefs = useMutation({
    mutationFn: savePrefsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 3000);
      toast({ title: "Customisation saved", description: "Your preferences have been updated." });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to save",
        description: getApiErrorMessage(err, "Try again."),
        variant: "destructive",
      });
    },
  });

  const saveAdjSilent = useMutation({
    mutationFn: savePrefsApi,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/profile"] }),
    onError: (err: unknown) => {
      toast({
        title: "Failed to save adjustment",
        description: getApiErrorMessage(err, "Try again."),
        variant: "destructive",
      });
    },
  });

  const handleAddCustomAdj = () => {
    if (!newAdj.label.trim()) return;
    const id = `custom-${Date.now()}`;
    const updated = [...prefs.customAdjustments, { id, label: newAdj.label.trim(), category: newAdj.category }];
    setPrefs((p) => ({ ...p, customAdjustments: updated }));
    setNewAdj({ label: "", category: "Instructional" });
    saveAdjSilent.mutate({ ...prefs, customAdjustments: updated });
  };

  const handleRemoveCustomAdj = (id: string) => {
    const updated = prefs.customAdjustments.filter((a) => a.id !== id);
    setPrefs((p) => ({ ...p, customAdjustments: updated }));
    saveAdjSilent.mutate({ ...prefs, customAdjustments: updated });
  };

  const toggleStandardAdj = (id: string) => {
    const hidden = prefs.hiddenAdjustmentIds.includes(id)
      ? prefs.hiddenAdjustmentIds.filter((i) => i !== id)
      : [...prefs.hiddenAdjustmentIds, id];
    const updated = { ...prefs, hiddenAdjustmentIds: hidden };
    setPrefs(updated);
    saveAdjSilent.mutate(updated);
  };

  const renameStandardAdj = (id: string, value: string) => {
    const original = NCCD_ADJUSTMENTS.find((a) => a.id === id)?.label ?? "";
    const renamed = { ...prefs.renamedAdjustments };
    const trimmed = value.trim();
    if (!trimmed || trimmed === original) {
      delete renamed[id];
    } else {
      renamed[id] = trimmed;
    }
    const updated = { ...prefs, renamedAdjustments: renamed };
    setPrefs(updated);
    saveAdjSilent.mutate(updated);
  };

  const toggleStandardCat = (cat: string) =>
    setOpenStandardCats((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });

  const toggleRecordType = (type: string) => {
    setPrefs((p) => {
      const enabled = p.enabledRecordTypes.includes(type);
      if (enabled && p.enabledRecordTypes.length === 1) return p; // keep at least one
      const next = enabled
        ? p.enabledRecordTypes.filter((t) => t !== type)
        : [...p.enabledRecordTypes, type];
      return {
        ...p,
        enabledRecordTypes: next,
        defaultRecordType: next.includes(p.defaultRecordType) ? p.defaultRecordType : next[0],
      };
    });
  };

  const updateProfile = useMutation({
    mutationFn: (data: typeof form & {
      schoolJoinAcknowledgedAt?: string;
      schoolJoinAcknowledgedSchoolCode?: string;
    }) =>
      customFetch<any>("/api/profile", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: (updated: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/school/access"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      // Refresh role and PIN from the updated response
      setCurrentSchoolRole(updated.schoolRole ?? "teacher");
      setCurrentHeadTeacherPin(updated.headTeacherPin ?? null);
      setForm((f) => ({ ...f, headTeacherPin: "" }));
      const becameHT = updated.schoolRole === "head_teacher";
      if (becameHT) {
        toast({ title: "Head teacher access granted", description: "You now have access to School Overview and the School access controls below." });
      } else {
        toast({ title: "Settings saved", description: "Your preferences have been updated." });
      }
    },
    onError: (err: unknown) => {
      // Surface the typed errors from PUT /api/profile (Task #44, #58, #85):
      // join codes (409) and acknowledgement codes (400) each get their own
      // actionable message read directly from `err.data.errorCode` /
      // `err.data.error` — no JSON-parsing of `Error.message` anywhere.
      const body = getApiErrorBody(err);
      const code = body?.errorCode;
      const serverMsg = body?.error;
      switch (code) {
        case "unknown_or_revoked":
          toast({
            title: "We don't recognise that school code",
            description: "Double-check it with your head teacher — codes are case-insensitive but include the dashes.",
            variant: "destructive",
          });
          return;
        case "domain_mismatch":
          toast({
            title: "This code is locked to your school's email",
            description: serverMsg ?? "Sign in with your school email and try again.",
            variant: "destructive",
          });
          return;
        case "seats_full":
          toast({
            title: "Your school has filled all its seats",
            description: serverMsg ?? "Ask your head teacher to request more seats.",
            variant: "destructive",
          });
          return;
        case "ACKNOWLEDGEMENT_PARTIAL":
          toast({
            title: "Privacy & terms acknowledgement is incomplete",
            description: "Tick the privacy and terms boxes together, then save again.",
            variant: "destructive",
          });
          return;
        case "ACKNOWLEDGEMENT_VERSION_MISMATCH":
          toast({
            title: "Our policies were just updated",
            description: "Refresh the page to load the current policy, then re-tick to save.",
            variant: "destructive",
          });
          return;
        case "SCHOOL_JOIN_ACK_PARTIAL":
        case "SCHOOL_JOIN_ACK_REQUIRED":
          toast({
            title: "Joining a school needs an explicit tick",
            description: "Tick the school-join confirmation next to the school code and save again.",
            variant: "destructive",
          });
          return;
        case "SCHOOL_JOIN_ACK_MISMATCH":
          toast({
            title: "School-join confirmation is out of date",
            description: "The code you ticked doesn't match the code you're saving. Re-tick the confirmation and save again.",
            variant: "destructive",
          });
          return;
      }
      if (err instanceof ApiError && err.status === 409) {
        toast({
          title: "Couldn't join that school",
          description: serverMsg ?? err.message,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Failed to save",
        description: getApiErrorMessage(err, "Try again."),
        variant: "destructive",
      });
    },
  });

  // Task #58 — does the form's schoolCode differ from the code the user
  // most recently acknowledged for joining (case-insensitive)? If so,
  // we must collect a fresh ack tick before the Save will go through.
  // The empty string is treated specially: clearing the school code is
  // not a "join", and the server auto-clears the ack columns in that
  // case, so no checkbox is shown.
  const lastAckedCode: string = (
    profile?.schoolJoinAcknowledgedSchoolCode ?? ""
  )
    .trim()
    .toUpperCase();
  const formSchoolCodeUpper = (form.schoolCode || "").trim().toUpperCase();
  const schoolJoinAckRequired =
    formSchoolCodeUpper !== "" && formSchoolCodeUpper !== lastAckedCode;

  // When the user types a new school code, blow away any stale ack tick
  // so the checkbox can never silently apply to a different code.
  useEffect(() => {
    setSchoolJoinAckTicked(false);
  }, [formSchoolCodeUpper]);

  const {
    data: preflight,
    isLoading: preflightLoading,
    refetch: refetchPreflight,
  } = useQuery({
    queryKey: ["rollover-preflight"],
    queryFn: () =>
      customFetch<RolloverPreflight>("/api/school-year/rollover/preflight"),
    enabled: activeTab === "school-year",
    staleTime: 30_000,
  });

  const saveSchoolYear = useMutation({
    mutationFn: (data: { activeSchoolYear: string | null }) =>
      customFetch<any>("/api/profile", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      setYearSaved(true);
      setTimeout(() => setYearSaved(false), 3000);
      toast({ title: "School year saved", description: "Your active school year has been updated." });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to save",
        description: getApiErrorMessage(err, "Try again."),
        variant: "destructive",
      });
    },
  });

  const handleSaveSchoolYear = () => {
    const trimmed = yearForm.activeSchoolYear.trim().slice(0, 50);
    saveSchoolYear.mutate({ activeSchoolYear: trimmed || null });
  };

  const handleSave = () => {
    if (schoolJoinAckRequired && !schoolJoinAckTicked) {
      toast({
        title: "Please confirm joining the school",
        description:
          "Tick the acknowledgement under the school code to continue.",
        variant: "destructive",
      });
      return;
    }
    const payload: typeof form & {
      schoolJoinAcknowledgedAt?: string;
      schoolJoinAcknowledgedSchoolCode?: string;
    } = { ...form };
    if (schoolJoinAckRequired && schoolJoinAckTicked) {
      payload.schoolJoinAcknowledgedAt = new Date().toISOString();
      payload.schoolJoinAcknowledgedSchoolCode = formSchoolCodeUpper;
    }
    updateProfile.mutate(payload);
  };

  const handleCopyPin = () => {
    if (currentHeadTeacherPin) {
      navigator.clipboard.writeText(currentHeadTeacherPin);
      setPinCopied(true);
      setTimeout(() => setPinCopied(false), 2000);
    }
  };

  const isTrial = (tierData as any)?.isTrial ?? false;
  const trialDaysLeft = (tierData as any)?.trialDaysLeft ?? 0;
  const hasActiveSubscription = (tierData as any)?.hasActiveSubscription ?? false;
  const hasAccess = (tierData as any)?.hasAccess ?? true;
  const betaAccessActive = (tierData as any)?.betaAccessActive === true;
  const isSubscribed = hasActiveSubscription;
  const planName = betaAccessActive
    ? "Founding Beta"
    : isTrial
      ? "Pro (Trial)"
      : hasActiveSubscription
        ? "Pro"
        : "Read-only";

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: "profile", label: "Profile & Region", icon: Globe },
    { id: "reports", label: "Reports & Exports", icon: FileText },
    { id: "appearance", label: "Appearance", icon: Palette },
    { id: "customisation", label: "Customisation", icon: Settings2 },
    { id: "school-year", label: "School Year", icon: CalendarDays },
    { id: "subscription", label: "Subscription", icon: CreditCard },
  ];

  if (isLoading) {
    return (
      <div className="p-6 max-w-3xl mx-auto flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Customise ClassTrack to suit your classroom and region.</p>
      </div>

      {/* Tab bar — flex-wrap lets tabs reflow to a second row at mid-widths
          instead of squishing all labels into one cramped line. */}
      <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1 mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-white shadow text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Profile & Region tab */}
      {activeTab === "profile" && (
        <div className="space-y-5">
          <SectionCard title="School & Organisation" icon={Building2}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="schoolName" className="text-sm font-medium text-gray-700">
                  School / Organisation name
                </Label>
                <Input
                  id="schoolName"
                  className="mt-1.5"
                  placeholder="e.g. Riverside Secondary College"
                  value={form.schoolName}
                  onChange={(e) => setForm({ ...form, schoolName: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  Appears in export file names and report headers.
                </p>
              </div>
              <div>
                <Label htmlFor="teachingLevel" className="text-sm font-medium text-gray-700">
                  Teaching level
                </Label>
                <Select value={form.teachingLevel} onValueChange={(v) => setForm({ ...form, teachingLevel: v })}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select teaching level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary / Elementary</SelectItem>
                    <SelectItem value="high_school">Secondary / High School</SelectItem>
                    <SelectItem value="k12">K–12</SelectItem>
                    <SelectItem value="tertiary">Tertiary / TAFE</SelectItem>
                    <SelectItem value="special_ed">Special Education</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">School & Head Teacher Access</p>
                  {(form.schoolCode || currentSchoolRole === "support_staff") && (
                    <Badge
                      className={`text-xs border-0 ${
                        currentSchoolRole === "head_teacher"
                          ? "bg-teal-100 text-teal-700"
                          : currentSchoolRole === "support_staff"
                            ? "bg-violet-100 text-violet-700"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {currentSchoolRole === "head_teacher" ? (
                        <><ShieldCheck className="h-3 w-3 mr-1" />Verified Head Teacher</>
                      ) : currentSchoolRole === "support_staff" ? (
                        "Support Staff"
                      ) : (
                        "Teacher"
                      )}
                    </Badge>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="schoolCode" className="text-sm font-medium text-gray-700">
                      School code
                    </Label>
                    <Input
                      id="schoolCode"
                      className="mt-1.5 font-mono"
                      placeholder="e.g. RIVERSIDE-2025"
                      value={form.schoolCode}
                      onChange={(e) => setForm({ ...form, schoolCode: e.target.value.toUpperCase() })}
                    />
                    <p className="text-xs text-gray-400 mt-1.5">
                      All teachers at your school enter the same code to be grouped. School codes are set up by your school during onboarding.
                    </p>
                  </div>

                  {/* Task #58 — school-join acknowledgement.
                      Only shown when the form's schoolCode differs from
                      the code the user most recently acknowledged
                      joining. Save is gated on this tick (see
                      handleSave). The checkbox resets whenever the
                      schoolCode field changes, so it can never silently
                      carry across to a different code. */}
                  {schoolJoinAckRequired && (
                    <div data-testid="school-join-ack-block" className="space-y-2">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Joining a school account may allow nominated
                        school leadership users to access school-level
                        record information and exports for teachers
                        linked to that school. Diary entries, lesson
                        plans, scratchpad notes and tasks are not
                        live-visible to head teachers or school
                        administrators in the current design.{" "}
                        <a
                          href="/privacy#school-access"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline"
                        >
                          Read the school-access section of the Privacy Policy
                        </a>
                        .
                      </p>
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <Checkbox
                          checked={schoolJoinAckTicked}
                          onCheckedChange={(v) =>
                            setSchoolJoinAckTicked(v === true)
                          }
                          className="mt-0.5"
                          data-testid="checkbox-school-join-ack"
                        />
                        <span className="text-xs text-gray-700 leading-relaxed">
                          I understand what changes when I join this
                          school account.
                        </span>
                      </label>
                    </div>
                  )}

                  {/* PIN field — only when a school code is entered and user isn't already head teacher */}
                  {form.schoolCode && currentSchoolRole !== "head_teacher" && (
                    <div>
                      <Label htmlFor="htPin" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        <KeyRound className="h-3.5 w-3.5 text-gray-400" />
                        Head Teacher PIN <span className="text-gray-400 font-normal">(optional)</span>
                      </Label>
                      <Input
                        id="htPin"
                        className="mt-1.5 font-mono tracking-widest"
                        placeholder="e.g. HT-XXXX-XXXX"
                        value={form.headTeacherPin}
                        onChange={(e) => setForm({ ...form, headTeacherPin: e.target.value.toUpperCase() })}
                      />
                      <p className="text-xs text-gray-400 mt-1.5">
                        Enter the PIN given to you by your school's head teacher to unlock School Overview access. Leave blank to join as a regular teacher.
                      </p>
                    </div>
                  )}

                  {/* Re-run school year setup — only relevant once a code is
                      actually SAVED (we key off the persisted profile, not
                      the unsaved form field, so a teacher who's typed a code
                      but hasn't hit Save can't trigger a 400 from the API)
                      AND the user hasn't already completed the wizard
                      (re-running after completion would amount to "edit join
                      date after run", which is explicitly out of scope).
                      AppLayout owns the wizard mount; we surface it here via
                      a window event so we don't need to lift state across
                      the page boundary. */}
                  {profile?.schoolCode && profile?.freshStartStatus !== "completed" && (
                    <div className="pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => {
                          window.dispatchEvent(
                            new CustomEvent("classtrack:open-fresh-start"),
                          );
                        }}
                        data-testid="button-rerun-fresh-start"
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        Re-run school year setup
                      </Button>
                      <p className="text-xs text-gray-400 mt-1.5">
                        Archive your current diary, hide your old private
                        classes/courses, and start a fresh diary for a new
                        school year.
                      </p>
                    </div>
                  )}

                  {/* Verified head teacher: regenerate-PIN flow.
                      The plain-text PIN is never stored on the server, so
                      we cannot show the existing one. Instead the head
                      teacher mints a fresh PIN here; the old one stops
                      working immediately. The new value is shown ONCE in
                      this card and is gone after a refresh. */}
                  {currentSchoolRole === "head_teacher" && (
                    <HeadTeacherPinCard
                      currentHeadTeacherPin={currentHeadTeacherPin}
                      onRegenerated={(pin) => setCurrentHeadTeacherPin(pin)}
                    />
                  )}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* School access controls — Head Teachers only (Task #44) */}
          {currentSchoolRole === "head_teacher" && (
            <SchoolAccessCard
              dismissedBanner={(profile as any)?.dismissedSchoolControlsBanner ?? false}
            />
          )}

          <SectionCard title="Region & Terminology" icon={Globe}>
            <div className="space-y-4">
              <InfoBox>
                Your region setting controls the terminology used throughout ClassTrack — record type labels,
                compliance system names, spelling, and export column headers. All your data stays the same;
                only the labels change.
              </InfoBox>

              <div>
                <Label className="text-sm font-medium text-gray-700">Region</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {ALL_REGIONS.map((r) => {
                    const config = REGION_CONFIG[r];
                    const isSelected = form.region === r;
                    return (
                      <button
                        key={r}
                        onClick={() => setForm({ ...form, region: r })}
                        className={`text-left p-4 rounded-xl border-2 transition-all ${
                          isSelected
                            ? "border-primary bg-teal-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xl">{config.flag}</span>
                          <span className={`text-sm font-semibold ${isSelected ? "text-primary" : "text-gray-800"}`}>
                            {config.label}
                          </span>
                          {isSelected && <CheckCircle2 className="h-4 w-4 text-primary ml-auto" />}
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">
                          {config.adjustmentType} · {config.behaviourSpelling} · {config.termUnit}s
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Preview of what changes */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">
                  With your current region settings, ClassTrack will use:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["Adjustment records", terms.adjustmentType],
                    ["Compliance system", terms.complianceSystem],
                    ["Behaviour / Behavior", terms.behaviourSpelling],
                    ["Time unit", terms.termUnit],
                    ["Year / Grade prefix", terms.yearLevelPrefix],
                    ["Export column header", terms.exportAdjustmentColHeader],
                  ].map(([key, value]) => (
                    <div key={key} className="flex flex-col">
                      <span className="text-xs text-gray-400">{key}</span>
                      <span className="text-xs font-medium text-gray-800">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SectionCard>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={
                updateProfile.isPending ||
                (schoolJoinAckRequired && !schoolJoinAckTicked)
              }
              className="gap-2"
            >
              {updateProfile.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saved ? "Saved!" : "Save settings"}
            </Button>
          </div>

          <DeleteAccountCard />
        </div>
      )}

      {/* Reports & Exports tab */}
      {activeTab === "reports" && (
        <div className="space-y-5">
          <SectionCard title="Report Header & Footer" icon={FileText}>
            <div className="space-y-4">
              <InfoBox>
                These texts appear at the top and bottom of exported reports and lesson plan PDFs.
                Use them to include your name, school, or any required compliance statement.
              </InfoBox>
              <div>
                <Label htmlFor="reportHeader" className="text-sm font-medium text-gray-700">
                  Report header
                </Label>
                <Textarea
                  id="reportHeader"
                  className="mt-1.5 resize-none"
                  rows={3}
                  placeholder={`e.g. Riverside Secondary College\nPrepared by: Ms J. Smith — Year 10 English\nConfidential — not for distribution`}
                  value={form.reportHeader}
                  onChange={(e) => setForm({ ...form, reportHeader: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="reportFooter" className="text-sm font-medium text-gray-700">
                  Report footer
                </Label>
                <Textarea
                  id="reportFooter"
                  className="mt-1.5 resize-none"
                  rows={2}
                  placeholder={`e.g. Generated by ClassTrack · classtrack.app`}
                  value={form.reportFooter}
                  onChange={(e) => setForm({ ...form, reportFooter: e.target.value })}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Date Format" icon={FileText}>
            <div>
              <Label className="text-sm font-medium text-gray-700">Date display format</Label>
              <Select value={form.dateFormat} onValueChange={(v) => setForm({ ...form, dateFormat: v })}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dd/MM/yyyy">DD/MM/YYYY — e.g. 26/04/2025 (AU, NZ)</SelectItem>
                  <SelectItem value="MM/dd/yyyy">MM/DD/YYYY — e.g. 04/26/2025 (US)</SelectItem>
                  <SelectItem value="yyyy-MM-dd">YYYY-MM-DD — e.g. 2025-04-26 (ISO)</SelectItem>
                  <SelectItem value="d MMM yyyy">D MMM YYYY — e.g. 26 Apr 2025</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400 mt-1.5">
                Affects how dates appear in exports and reports. The app diary always shows full date labels.
              </p>
            </div>
          </SectionCard>

          <SectionCard title="What's included in exports" icon={FileText}>
            <div className="space-y-3">
              <InfoBox>
                All exports include: date, period, record type, class, subject, students, notes, and adjustments.
                The columns below show how they're labelled based on your region setting.
              </InfoBox>
              <div className="space-y-2">
                {[
                  { col: "Record type label", value: terms.adjustmentType },
                  { col: "Adjustment column", value: terms.exportAdjustmentColHeader },
                  { col: "Upload status column", value: terms.uploadedToSystem },
                  { col: "School name in filename", value: form.schoolName ? `"${form.schoolName}"` : "Not set — set school name on the Profile tab" },
                ].map(({ col, value }) => (
                  <div key={col} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-gray-600">{col}</span>
                    <span className={`text-sm font-medium ${form.schoolName || col !== "School name in filename" ? "text-gray-800" : "text-amber-600"}`}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={updateProfile.isPending} className="gap-2">
              {updateProfile.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saved ? "Saved!" : "Save settings"}
            </Button>
          </div>
        </div>
      )}

      {activeTab === "appearance" && <AppearanceTab />}

      {/* School Year tab */}
      {activeTab === "school-year" && (
        <div className="space-y-5">
          <SectionCard title="Active School Year" icon={CalendarDays}>
            <div className="space-y-4">
              <InfoBox>
                Set your current school year label here (e.g. "2026" or "2026–2027"). This will be used in
                upcoming year-end rollover features to tag and organise your classes, timetables, and
                programs by year.
              </InfoBox>
              <div>
                <Label htmlFor="activeSchoolYear" className="text-sm font-medium text-gray-700">
                  School year
                </Label>
                <Input
                  id="activeSchoolYear"
                  className="mt-1.5"
                  placeholder="e.g. 2026 or 2026–2027"
                  value={yearForm.activeSchoolYear}
                  maxLength={50}
                  onChange={(e) => setYearForm({ activeSchoolYear: e.target.value })}
                  data-testid="input-active-school-year"
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  Leave blank if you don't need year-based organisation yet.
                </p>
              </div>
            </div>
          </SectionCard>
          <div className="flex justify-end">
            <Button
              onClick={handleSaveSchoolYear}
              disabled={saveSchoolYear.isPending}
              className="gap-2"
              data-testid="button-save-school-year"
            >
              {saveSchoolYear.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : yearSaved ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {yearSaved ? "Saved!" : "Save school year"}
            </Button>
          </div>
          <PreflightSection
            preflight={preflight}
            isLoading={preflightLoading}
            onRefresh={refetchPreflight}
          />
          {preflight &&
            (preflight.blocking.classesWithoutSchoolYear ||
              preflight.blocking.registersWithoutSchoolYear) && (
              <FixUnlabelledSection
                activeSchoolYear={preflight.activeSchoolYear}
                onFixed={refetchPreflight}
              />
            )}
          <StudentReviewSection onArchiveIdsChange={setStudentArchiveIds} />
          <DryRunSection
            activeSchoolYear={preflight?.activeSchoolYear ?? null}
            studentArchiveIds={studentArchiveIds}
            onRolloverSuccess={() => setJustRolledOver(true)}
          />
          <NewYearSetupChecklist
            preflight={preflight}
            justRolledOver={justRolledOver}
          />
        </div>
      )}

      {/* Customisation tab */}
      {activeTab === "customisation" && (
        <div className="space-y-5">

          <SectionCard title="Student Display" icon={Settings2}>
            <div className="space-y-3">
              <InfoBox>
                Choose the default way student names appear across ClassTrack — full name, first name only, initials,
                preferred name, or a student code. You can still override this per student when you add them.
                Reduce identifiers when you're sharing your screen, exporting records, or working in a public space.
              </InfoBox>
              <div className="flex flex-wrap items-center gap-3">
                <label htmlFor="default-display-mode" className="text-sm font-medium text-gray-800">
                  Default student display
                </label>
                <select
                  id="default-display-mode"
                  className="text-sm rounded-md border border-gray-200 bg-white px-2.5 py-1.5 focus:border-teal-400 focus:outline-none"
                  value={prefs.defaultStudentDisplayMode}
                  onChange={(e) => {
                    const next = { ...prefs, defaultStudentDisplayMode: e.target.value as typeof prefs.defaultStudentDisplayMode };
                    setPrefs(next);
                    saveAdjSilent.mutate(next);
                  }}
                  data-testid="select-default-display-mode"
                >
                  <option value="full">Full name (First Last)</option>
                  <option value="first">First name only</option>
                  <option value="initials">Initials (F.L.)</option>
                  <option value="nickname">Preferred / nickname</option>
                  <option value="code">Student code</option>
                </select>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Record Types" icon={Settings2}>
            <div className="space-y-4">
              <InfoBox>
                Choose which record types appear when you create a new record. At least one must remain enabled.
                Your most-used type can be set as the default.
              </InfoBox>
              <div className="space-y-2">
                {ALL_RECORD_TYPES.map(({ value, label }) => {
                  const enabled = prefs.enabledRecordTypes.includes(value);
                  return (
                    <div key={value} className="flex items-center justify-between py-2 px-3 rounded-lg border border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id={`rt-${value}`}
                          checked={enabled}
                          onChange={() => toggleRecordType(value)}
                          className="h-4 w-4 rounded border-gray-300 text-primary accent-teal-600"
                        />
                        <label htmlFor={`rt-${value}`} className="text-sm font-medium text-gray-800 cursor-pointer">
                          {label}
                        </label>
                      </div>
                      {enabled && (
                        <button
                          type="button"
                          onClick={() => setPrefs((p) => ({ ...p, defaultRecordType: value }))}
                          className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                            prefs.defaultRecordType === value
                              ? "bg-primary text-white border-primary"
                              : "border-gray-300 text-gray-500 hover:border-primary hover:text-primary"
                          }`}
                        >
                          {prefs.defaultRecordType === value ? "Default" : "Set default"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Standard Adjustment Bank" icon={Library}>
            <div className="space-y-4">
              <InfoBox>
                These are the built-in NCCD adjustments available in ClassTrack. Toggle items on or off to match
                what your school uses (e.g. to align with Sentral), and click any label to rename it to match
                your school's exact terminology. Changes take effect immediately in the record form.
              </InfoBox>
              <div className="space-y-1.5">
                {Object.entries(groupByCategory(NCCD_ADJUSTMENTS)).map(([cat, adjs]) => {
                  const visibleCount = adjs.filter((a) => !prefs.hiddenAdjustmentIds.includes(a.id)).length;
                  const isOpen = openStandardCats.has(cat);
                  return (
                    <div key={cat} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleStandardCat(cat)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 transition-colors"
                      >
                        <span>{cat}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{visibleCount}/{adjs.length} visible</span>
                          {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="divide-y divide-gray-50">
                          {adjs.map((adj) => {
                            const hidden = prefs.hiddenAdjustmentIds.includes(adj.id);
                            const currentLabel = prefs.renamedAdjustments[adj.id] ?? adj.label;
                            const isRenamed = !!prefs.renamedAdjustments[adj.id] && prefs.renamedAdjustments[adj.id] !== adj.label;
                            return (
                              <div key={adj.id} className={`flex items-start gap-3 px-3 py-2 transition-colors ${hidden ? "bg-gray-50 opacity-60" : "bg-white"}`}>
                                <input
                                  type="checkbox"
                                  checked={!hidden}
                                  onChange={() => toggleStandardAdj(adj.id)}
                                  className="h-4 w-4 mt-1.5 rounded accent-teal-600 flex-shrink-0 cursor-pointer"
                                />
                                <div className="flex-1 min-w-0">
                                  <input
                                    type="text"
                                    value={currentLabel}
                                    disabled={hidden}
                                    onChange={(e) => setPrefs((p) => ({
                                      ...p,
                                      renamedAdjustments: { ...p.renamedAdjustments, [adj.id]: e.target.value },
                                    }))}
                                    onBlur={(e) => renameStandardAdj(adj.id, e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                                    className="w-full text-sm bg-transparent border-0 border-b border-transparent focus:border-teal-400 focus:outline-none py-0.5 transition-colors disabled:cursor-not-allowed"
                                    title="Click to rename"
                                  />
                                  {isRenamed && (
                                    <p className="text-[11px] text-gray-400 mt-0.5">Original: {adj.label}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Additional Adjustments" icon={Plus}>
            <div className="space-y-4">
              <InfoBox>
                Add any adjustments specific to your school or programme that aren't in the standard bank above.
                They appear alongside the standard list when creating adjustment records.
              </InfoBox>

              <div className="space-y-2">
                {prefs.customAdjustments.length === 0 ? (
                  <p className="text-sm text-gray-400 italic py-1">No custom adjustments added yet.</p>
                ) : (
                  prefs.customAdjustments.map((adj) => (
                    <div key={adj.id} className="flex items-center justify-between py-2 px-3 rounded-lg border border-gray-100 bg-gray-50">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{adj.label}</p>
                        <p className="text-xs text-gray-400">{adj.category}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomAdj(adj.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="border border-dashed border-gray-300 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add custom adjustment</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. Buddy reader support"
                    value={newAdj.label}
                    onChange={(e) => setNewAdj((n) => ({ ...n, label: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && handleAddCustomAdj()}
                    className="flex-1 text-sm"
                  />
                  <Select value={newAdj.category} onValueChange={(v) => setNewAdj((n) => ({ ...n, category: v }))}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NCCD_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" onClick={handleAddCustomAdj} variant="outline" size="sm" className="gap-1 shrink-0">
                    <Plus className="h-4 w-4" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </SectionCard>

          <div className="flex justify-end">
            <Button onClick={() => savePrefs.mutate(prefs)} disabled={savePrefs.isPending} className="gap-2">
              {savePrefs.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : prefsSaved ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {prefsSaved ? "Saved!" : "Save customisation"}
            </Button>
          </div>
        </div>
      )}

      {/* Subscription tab */}
      {activeTab === "subscription" && (
        <div className="space-y-5">
          <SectionCard title="Current plan" icon={CreditCard}>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-teal-50 rounded-xl border border-teal-100">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-gray-900">
                      {planName}
                    </span>
                    <Badge
                      className={
                        hasAccess
                          ? "bg-primary/10 text-primary border-0 text-xs"
                          : "bg-rose-100 text-rose-700 border-0 text-xs"
                      }
                    >
                      {isTrial
                        ? `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left`
                        : hasAccess
                          ? "Active"
                          : "Subscribe to make changes"}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {betaAccessActive
                      ? "Founding beta access — no payment is needed during beta."
                      : isTrial
                        ? "Full access during your trial. No credit card required yet."
                        : hasActiveSubscription
                          ? "Individual Pro plan — billed annually."
                          : "Your trial has ended. Subscribe to start making changes again — your data is safe and exports still work."}
                  </p>
                </div>
              </div>

              {betaAccessActive ? (
                <div className="p-4 rounded-xl border border-teal-200 bg-teal-50">
                  <p className="text-sm font-medium text-teal-800 mb-1">
                    You have founding beta access until 1 Jan 2027.
                  </p>
                  <p className="text-sm text-teal-700">
                    No payment is needed during beta. Pricing will be confirmed before beta ends.
                  </p>
                </div>
              ) : !isSubscribed ? (
                <div className="p-4 rounded-xl border border-amber-200 bg-amber-50">
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    {isTrial ? `Trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"}` : "Ready to subscribe?"}
                  </p>
                  <p className="text-sm text-amber-700 mb-3">
                    {isTrial
                      ? "Subscribe now to keep full access after your trial — no lock-in."
                      : "Pro gives you full access to everything, with no lock-in."}
                  </p>
                  <Button
                    size="sm"
                    onClick={handleSubscribe}
                    disabled={createCheckout.isPending}
                    className="gap-2"
                  >
                    {createCheckout.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Subscribe to Pro
                  </Button>
                </div>
              ) : null}

              {isSubscribed && (
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    Manage your billing, update payment details, or cancel your subscription through the billing portal.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={handleManageBilling}
                    disabled={openPortal.isPending}
                  >
                    {openPortal.isPending
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <ExternalLink className="h-4 w-4" />
                    }
                    Manage billing
                  </Button>
                </div>
              )}

              <div className={`p-4 rounded-xl border bg-gray-50 ${aiSoftLimitReached && !aiMonthlyLimitEnforced ? "border-amber-300" : "border-gray-200"}`}>
                <p className="text-sm font-medium text-gray-700 mb-1">AI usage</p>
                <p className="text-sm text-gray-500">
                  AI calls this month:{" "}
                  <span className="font-medium text-gray-700">
                    {aiUsedThisMonth} of {aiMonthlyLimit}
                  </span>
                </p>
                {aiSoftLimitReached && !aiMonthlyLimitEnforced && (
                  <p className="text-xs text-amber-700 mt-1.5">
                    You've used your included AI calls this month — usage continues during beta.
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {hasAccess
                    ? "AI support for notes and Program Registration is included on your plan. We track per-month totals so we can investigate any unusual cost spikes."
                    : "AI polish and lesson prep are paused while your account is read-only. Subscribe to start using them again — your existing usage history is kept."}
                </p>
                <details className="group mt-2">
                  <summary className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer select-none list-none inline-flex items-center gap-1">
                    <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
                    What does this count?
                  </summary>
                  <ul className="mt-2 space-y-1.5 text-xs text-gray-500 list-disc pl-5">
                    <li>Each successful AI polish click is one call.</li>
                    <li>Each successful lesson-prep generation is one call.</li>
                    <li>Each Program Registration AI import or evaluation is one call.</li>
                    <li>Failed requests aren't counted — only what actually came back.</li>
                    <li>Your count resets at the start of each calendar month.</li>
                  </ul>
                </details>
              </div>

              <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                <p className="text-sm font-medium text-gray-700 mb-1">School pack enquiries</p>
                <p className="text-sm text-gray-500">
                  Whole-school plans, multi-staff billing, or onboarding support — contact us at{" "}
                  <a href="mailto:classtrack.com.au@gmail.com" className="text-primary underline">
                    classtrack.com.au@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Data & privacy" icon={FileText}>
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                Your data is stored securely and never sold or shared with third parties.
                See our <a href="/privacy" className="text-primary underline" target="_blank">Privacy Policy</a> and{" "}
                <a href="/terms" className="text-primary underline" target="_blank">Terms of Service</a> for full details.
              </p>
              <p className="text-xs text-gray-400">
                Application database: Australia (Neon, AWS Sydney). API server and file storage:
                United States (Replit infrastructure). Migration of remaining infrastructure to
                Australian hosting is planned before any school contracts are entered into.
              </p>
            </div>
          </SectionCard>
        </div>
      )}

    </div>
  );
}

// ── Task #137: Delete account ────────────────────────────────────────────
//
// Two-step destructive flow. The user must:
//   (1) click "Delete my account" to open the confirm dialog, AND
//   (2) type the literal phrase "DELETE MY ACCOUNT" into the input
//       and click the red "Permanently delete" button to fire the
//       request.
//
// On success we sign the user out of Clerk on the client so the
// banned session is gone immediately even if the server-side revoke
// is still in flight.
function DeleteAccountCard() {
  const { toast } = useToast();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const CONFIRM_PHRASE = "DELETE MY ACCOUNT";
  const phraseOk = confirmText.trim() === CONFIRM_PHRASE;

  async function handleDelete() {
    setSubmitting(true);
    try {
      await customFetch<{ ok: boolean }>("/api/profile/delete", {
        method: "POST",
        body: JSON.stringify(reason ? { reason } : {}),
        headers: { "Content-Type": "application/json" },
      });
      toast({
        title: "Account scheduled for deletion",
        description:
          "Your account will be permanently deleted in 30 days. Contact support within that window to restore it.",
      });
      try {
        await signOut({ redirectUrl: "/" });
      } catch {
        window.location.href = "/";
      }
    } catch (err) {
      toast({
        title: "Could not delete account",
        description: getApiErrorMessage(err, "Please try again in a moment."),
        variant: "destructive",
      });
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-red-200 p-6">
      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-red-100">
        <AlertCircle className="h-4 w-4 text-red-600" />
        <h2 className="text-sm font-semibold text-red-700 uppercase tracking-wide">
          Delete account
        </h2>
      </div>

      {!open ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Permanently delete your ClassTrack account, lesson plans, records and
            settings. We mark your account for deletion immediately and prevent
            further sign-ins. The data is purged from our active systems
            <strong> 30 days </strong>
            later. You can ask us to restore the account by contacting support
            during that window.
          </p>
          <p className="text-xs text-gray-500">
            School-shared students, classes and courses you contributed to your
            school's shared records will remain available to your school
            colleagues but will be dissociated from your account.
          </p>
          <div className="pt-2">
            <Button
              variant="destructive"
              onClick={() => setOpen(true)}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete my account
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-800">
            This action cannot be undone after 30 days. Any active subscription
            will be cancelled at the end of the current billing period and you
            will be signed out of every device.
          </div>
          <div>
            <Label htmlFor="deleteReason" className="text-sm font-medium text-gray-700">
              Reason (optional — helps us improve the product)
            </Label>
            <Textarea
              id="deleteReason"
              className="mt-1.5"
              rows={2}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Tell us why you're leaving (optional)"
            />
          </div>
          <div>
            <Label htmlFor="deleteConfirm" className="text-sm font-medium text-gray-700">
              Type <span className="font-mono text-red-700">{CONFIRM_PHRASE}</span> to confirm
            </Label>
            <Input
              id="deleteConfirm"
              className="mt-1.5 font-mono"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              autoComplete="off"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                setConfirmText("");
                setReason("");
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!phraseOk || submitting}
              onClick={handleDelete}
              className="gap-2"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Permanently delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AppearancePreview({
  resolvedMode,
}: {
  resolvedMode: "light" | "dark";
}) {
  return (
    <div
      className="app-pattern-host rounded-xl border border-gray-200 overflow-hidden shadow-sm"
      data-testid="appearance-preview"
    >
      <div className="flex h-56" style={{ background: "hsl(var(--background))" }}>
        <div
          className="w-32 flex flex-col gap-2 p-3"
          style={{
            background: "hsl(var(--sidebar))",
            color: "hsl(var(--sidebar-foreground))",
          }}
        >
          <div className="text-xs font-semibold opacity-80">ClassTrack</div>
          <div
            className="rounded-md px-2 py-1.5 text-xs font-medium"
            style={{
              background: "hsl(var(--sidebar-accent))",
              color: "hsl(var(--sidebar-accent-foreground))",
            }}
          >
            Lessons
          </div>
          <div className="rounded-md px-2 py-1.5 text-xs opacity-70">Classes</div>
          <div className="rounded-md px-2 py-1.5 text-xs opacity-70">Records</div>
        </div>
        <div className="flex-1 p-4 flex flex-col gap-3" style={{ color: "hsl(var(--foreground))" }}>
          <div
            className="text-base font-semibold"
            style={{ fontFamily: "var(--app-font-heading)" }}
          >
            Year 5 — Term 2
          </div>
          <div
            className="text-xs opacity-70"
            style={{ fontFamily: "var(--app-font-sans)" }}
          >
            Preview updates instantly as you change the options below.
            Switching to {resolvedMode} mode? You'll see it here first.
          </div>
          <div
            className="rounded-lg p-3 text-xs"
            style={{
              background: "hsl(var(--card))",
              color: "hsl(var(--card-foreground))",
              border: "1px solid hsl(var(--border))",
            }}
          >
            <div className="font-medium mb-1">Today's lesson</div>
            <div className="opacity-70">Fractions of a whole — group rotation</div>
          </div>
          <div className="mt-auto flex gap-2">
            <button
              type="button"
              className="px-3 py-1.5 rounded-md text-xs font-medium"
              style={{
                background: "hsl(var(--primary))",
                color: "hsl(var(--primary-foreground))",
              }}
            >
              Save lesson
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded-md text-xs font-medium"
              style={{
                background: "transparent",
                color: "hsl(var(--foreground))",
                border: "1px solid hsl(var(--border))",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppearanceTab() {
  const { prefs, update, reset, resolvedMode } = useAppearance();

  const ModeButton = ({
    value,
    label,
    Icon,
  }: {
    value: "light" | "dark" | "system";
    label: string;
    Icon: React.ElementType;
  }) => (
    <button
      type="button"
      onClick={() => update({ mode: value })}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-all ${
        prefs.mode === value
          ? "bg-white shadow text-gray-900"
          : "text-gray-500 hover:text-gray-700"
      }`}
      data-testid={`appearance-mode-${value}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );

  return (
    <div className="space-y-5">
      <SectionCard title="Live preview" icon={Palette}>
        <AppearancePreview resolvedMode={resolvedMode} />
        <p className="text-xs text-gray-500 mt-2">
          A miniature view of the app shell — sidebar, headings, body
          text and primary button — using your current selections.
        </p>
      </SectionCard>

      <SectionCard title="Skin" icon={Palette}>
        <div className="space-y-4">
          <InfoBox>
            Pick a curated palette and feel for ClassTrack. Each skin pairs colours, a
            sidebar style and a default accent — you can still tweak the accent, font and
            pattern below. Existing default is <strong>Classic Teal</strong>.
          </InfoBox>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {SKINS.map((skin) => {
              const active = prefs.skin === skin.id;
              return (
                <button
                  key={skin.id}
                  type="button"
                  onClick={() => update({ skin: skin.id, accent: skin.defaultAccent })}
                  className={`text-left rounded-xl border-2 transition-all overflow-hidden ${
                    active
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid={`appearance-skin-${skin.id}`}
                >
                  <div
                    className="h-20 flex"
                    style={{ background: skin.preview.bg, color: skin.preview.text }}
                  >
                    <div
                      className="w-1/3 flex flex-col gap-1.5 p-1.5"
                      style={{ background: skin.preview.sidebar }}
                    >
                      <div className="h-1.5 rounded-full bg-white/30" />
                      <div className="h-1.5 rounded-full bg-white/20" />
                      <div className="h-1.5 rounded-full bg-white/20" />
                    </div>
                    <div className="flex-1 p-1.5 flex flex-col gap-1">
                      <div
                        className="h-1.5 rounded-full opacity-70"
                        style={{ background: skin.preview.text }}
                      />
                      <div
                        className="h-1.5 w-2/3 rounded-full opacity-50"
                        style={{ background: skin.preview.text }}
                      />
                      <div className="flex-1" />
                      <div
                        className="h-3 w-12 rounded-md self-end"
                        style={{ background: skin.preview.accent }}
                      />
                    </div>
                  </div>
                  <div className="p-2 bg-white">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800">{skin.label}</span>
                      {active && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">
                      {skin.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Light or Dark" icon={resolvedMode === "dark" ? Moon : Sun}>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 max-w-md">
          <ModeButton value="light" label="Light" Icon={Sun} />
          <ModeButton value="dark" label="Dark" Icon={Moon} />
          <ModeButton value="system" label="System" Icon={Monitor} />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {prefs.mode === "system"
            ? `Following your device — currently ${resolvedMode}.`
            : "Override your device setting and force this mode."}
        </p>
      </SectionCard>

      <SectionCard title="Accent colour" icon={Palette}>
        <p className="text-xs text-gray-500 mb-3">
          Used for primary buttons, links and focus rings. Picking a skin sets a sensible
          default — change here if you want something different.
        </p>
        <div className="flex flex-wrap gap-2">
          {ACCENTS.map((accent) => {
            const active = prefs.accent === accent.id;
            return (
              <button
                key={accent.id}
                type="button"
                onClick={() => update({ accent: accent.id })}
                title={accent.label}
                className={`group relative h-10 w-10 rounded-full transition-all ${
                  active ? "ring-2 ring-offset-2 ring-gray-700 scale-110" : "hover:scale-105"
                }`}
                style={{ background: `hsl(${accent.hsl})` }}
                data-testid={`appearance-accent-${accent.id}`}
              >
                {active && (
                  <CheckCircle2
                    className="h-4 w-4 absolute inset-0 m-auto"
                    style={{ color: `hsl(${accent.foregroundHsl})` }}
                  />
                )}
                <span className="sr-only">{accent.label}</span>
              </button>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="Font pairing" icon={FileText}>
        <div className="space-y-2">
          {FONT_PAIRINGS.map((pairing) => {
            const active = prefs.fontPairing === pairing.id;
            return (
              <button
                key={pairing.id}
                type="button"
                onClick={() => update({ fontPairing: pairing.id })}
                className={`w-full text-left rounded-lg border-2 p-3 transition-all ${
                  active
                    ? "border-primary bg-primary/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`appearance-font-${pairing.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-800">{pairing.label}</span>
                  {active && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                </div>
                <p
                  className="text-base text-gray-700"
                  style={{ fontFamily: pairing.heading }}
                >
                  Aa Bb Cc — Lesson plans
                </p>
                <p
                  className="text-xs text-gray-500"
                  style={{ fontFamily: pairing.body }}
                >
                  {pairing.sample}
                </p>
              </button>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="Background pattern" icon={Settings2}>
        <p className="text-xs text-gray-500 mb-3">
          A subtle texture behind the main canvas. Kept low-opacity so it never fights with
          your content, and never shows on printable exports.
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {PATTERNS.map((pattern) => {
            const active = prefs.pattern === pattern.id;
            return (
              <button
                key={pattern.id}
                type="button"
                onClick={() => update({ pattern: pattern.id })}
                className={`rounded-lg border-2 transition-all overflow-hidden ${
                  active
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`appearance-pattern-${pattern.id}`}
              >
                <div
                  className="h-14 bg-gray-50"
                  style={{
                    backgroundImage: pattern.url || "none",
                    backgroundRepeat: "repeat",
                    color: "hsl(var(--foreground))",
                    opacity: pattern.id === "none" ? 1 : 0.6,
                  }}
                >
                  {pattern.id === "none" && (
                    <div className="h-full flex items-center justify-center text-xs text-gray-400">
                      None
                    </div>
                  )}
                </div>
                <div className="p-1.5 bg-white text-center text-[11px] font-medium text-gray-600">
                  {pattern.label}
                </div>
              </button>
            );
          })}
        </div>
      </SectionCard>

      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={reset}
          className="gap-2"
          data-testid="appearance-reset"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to defaults
        </Button>
      </div>
    </div>
  );
}
